import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuArrowLeft, LuArrowUpRight, LuCalendar, LuBuilding2, LuCompass, LuUser } from 'react-icons/lu';
import { findProjectForRoute, normalizeSlug } from '../utils/portfolioMatching';
import { portfolio as staticPortfolio } from '../data/portfolio';
import { publicApi } from '../services/publicApi';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';

function encodeImageSrc(src) {
  if (!src || typeof src !== 'string') return src;
  if (/^https?:\/\//i.test(src) || src.startsWith('data:')) return src;
  return src.replace(/ /g, '%20');
}

function mapApiProject(item) {
  const galleryImages = Array.isArray(item.galleryImages)
    ? item.galleryImages.map((entry) => {
        if (typeof entry === 'string') {
          return {
            url: entry,
            label: '',
            size: 'wide',
            colSpan: 6,
            rowSpan: 1,
            width: undefined,
            height: undefined,
            unit: 'px',
          };
        }
        return {
          url: entry?.url || entry?.src || '',
          label: entry?.label || '',
          size: entry?.size || 'wide',
          colSpan: entry?.colSpan ?? 6,
          rowSpan: entry?.rowSpan ?? 1,
          width: entry?.width,
          height: entry?.height,
          unit: entry?.unit || 'px',
        };
      }).filter((entry) => entry.url)
    : [];

  return {
    id: item.id,
    slug: item.slug,
    title: item.title,
    category: item.category || 'Project',
    description: item.description,
    longDescription: item.longDescription || item.description,
    clientName: item.clientName,
    industry: item.industry || item.category,
    year: item.year ? String(item.year) : '2025',
    direction: item.projectDirection || 'Project Delivery',
    websiteUrl: item.websiteUrl,
    image: item.mainImageUrl || '/img/pos system.png',
    galleryImages,
    detailFields: Array.isArray(item.detailFields) ? item.detailFields : [],
  };
}

function mapStaticProject(item) {
  const galleryImages = (item.galleryImages || (item.image ? [item.image] : [])).map((entry) => {
    if (typeof entry === 'string') {
      return { url: entry, label: '', size: 'wide' };
    }
    return entry;
  });

  return {
    ...item,
    slug: item.slug || String(item.id),
    longDescription: item.longDescription || item.description,
    clientName: item.clientName,
    websiteUrl: item.websiteUrl,
    galleryImages,
    detailFields: item.detailFields || [],
  };
}

const PortfolioDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadProject = async () => {
      setLoading(true);
      try {
        const [projects, overviews] = await Promise.all([
          publicApi.getProjects(),
          publicApi.getPortfolioOverviews(),
        ]);

        if (isMounted) {
          const found = findProjectForRoute(projects, overviews, slug);
          if (found) {
            setProject(mapApiProject(found));
            return;
          }
        }
      } catch {
        // Fall back to static content.
      }

      if (isMounted) {
        const found = staticPortfolio.find(
          (item) =>
            normalizeSlug(item.slug || item.id) === normalizeSlug(slug)
            || String(item.id) === String(slug),
        );
        setProject(found ? mapStaticProject(found) : null);
      }
    };

    loadProject().finally(() => {
      if (isMounted) setLoading(false);
    });

    return () => {
      isMounted = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 pt-24 transition-colors duration-300 dark:bg-black">
        <p className="text-gray-500 dark:text-gray-300">Loading project...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 pt-24 transition-colors duration-300 dark:bg-black">
        <h2 className="mb-4 text-3xl font-bold text-black dark:text-white">Project Not Found</h2>
        <Link to="/#portfolio">
          <Button variant="primary">Back to Portfolio</Button>
        </Link>
      </div>
    );
  }

  const galleryUrls = (project.galleryImages || [])
    .map((entry) => (typeof entry === 'string' ? entry : entry?.url || entry?.src))
    .filter(Boolean);
  const gallery = galleryUrls.length ? galleryUrls : project.image ? [project.image] : [];

  const renderDetailField = (field, index) => {
    if (!field?.label && !field?.value) return null;

    if (field.type === 'image' && field.value) {
      return (
        <div key={index} className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10">
          <p className="bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:bg-[#111] dark:text-gray-400">
            {field.label}
          </p>
          <img src={encodeImageSrc(field.value)} alt={field.label} className="h-56 w-full object-cover" />
        </div>
      );
    }

    if (field.type === 'url' && field.value) {
      return (
        <div key={index} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#111]">
          <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            {field.label}
          </p>
          <a
            href={field.value}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 break-all font-semibold text-primary hover:underline"
          >
            {field.value}
            <LuArrowUpRight size={16} />
          </a>
        </div>
      );
    }

    return (
      <div key={index} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#111]">
        <p className="mb-1 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {field.label}
        </p>
        <p className="text-gray-700 dark:text-gray-200">{field.value}</p>
      </div>
    );
  };

  const textDetailFields = (project.detailFields || []).filter(
    (field) => field?.label || field?.value,
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-28 transition-colors duration-300 dark:bg-black">
      <div className="mx-auto max-w-6xl px-6">
        <Link
          to="/#portfolio"
          className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-primary dark:text-gray-300"
        >
          <LuArrowLeft size={16} />
          Back to Portfolio
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <Badge className="mb-4 rounded-full bg-primary px-3 py-1 text-xs font-extrabold tracking-wider text-black">
            {project.category}
          </Badge>
          <h1 className="mb-4 text-4xl font-extrabold text-black dark:text-white md:text-5xl">
            {project.title}
          </h1>
          <p className="max-w-3xl text-lg leading-relaxed text-gray-600 dark:text-gray-300">
            {project.longDescription}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="mb-10 overflow-hidden rounded-[2rem] border border-gray-200 shadow-[0_20px_60px_rgba(0,0,0,0.08)] dark:border-white/10"
        >
          <img
            src={encodeImageSrc(project.image)}
            alt={project.title}
            className="max-h-[520px] w-full object-cover"
          />
        </motion.div>

        <div className="mb-10 grid grid-cols-1 gap-6 lg:grid-cols-4">
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#0f0f0f]">
            <div className="mb-2 flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <LuCalendar size={18} />
              <span className="text-xs font-bold uppercase">Year</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.year}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#0f0f0f]">
            <div className="mb-2 flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <LuBuilding2 size={18} />
              <span className="text-xs font-bold uppercase">Industry</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.industry}</p>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#0f0f0f]">
            <div className="mb-2 flex items-center gap-3 text-gray-500 dark:text-gray-400">
              <LuCompass size={18} />
              <span className="text-xs font-bold uppercase">Direction</span>
            </div>
            <p className="text-xl font-bold text-black dark:text-white">{project.direction}</p>
          </div>
          {project.clientName ? (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#0f0f0f]">
              <div className="mb-2 flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <LuUser size={18} />
                <span className="text-xs font-bold uppercase">Client</span>
              </div>
              <p className="text-xl font-bold text-black dark:text-white">{project.clientName}</p>
            </div>
          ) : null}
        </div>

        {project.websiteUrl && project.websiteUrl !== '#' ? (
          <div className="mb-10 rounded-2xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-[#0f0f0f]">
            <p className="mb-2 text-xs font-bold uppercase text-gray-500 dark:text-gray-400">Website</p>
            <a
              href={project.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-black hover:text-primary"
            >
              Visit Project
              <LuArrowUpRight size={16} />
            </a>
          </div>
        ) : null}

        {textDetailFields.length > 0 ? (
          <div className="mb-10">
            <h2 className="mb-4 text-2xl font-extrabold text-black dark:text-white">Project Details</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {textDetailFields.map(renderDetailField)}
            </div>
          </div>
        ) : null}

        {gallery.length > 1 ? (
          <div>
            <h2 className="mb-4 text-2xl font-extrabold text-black dark:text-white">Gallery</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              {gallery.map((image, index) => (
                <div
                  key={`${image}-${index}`}
                  className="overflow-hidden rounded-2xl border border-gray-200 dark:border-white/10"
                >
                  <img
                    src={encodeImageSrc(image)}
                    alt={`${project.title} ${index + 1}`}
                    className="h-64 w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PortfolioDetail;
